import React, { useState, useEffect } from "react";
import { Card, Descriptions, Tag, Button, Space, Badge } from "antd";
import {
  ArrowLeftOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TagOutlined,
  GiftOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { useGet, useDynamicApi } from "../../hooks/useApi";
import { API_ENDPOINTS } from "../../config/api";
import Toast from "../../utils/toast";
import { useTranslation } from "react-i18next";

const ClubMigrateDetails = () => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const [item, setItem] = useState(null);
  const [loading, setLoading] = useState(true);
  const [clubsList, setClubsList] = useState([]);

  // API hooks
  const { execute: getItem, loading: itemLoading } = useDynamicApi("GET");
  const { execute: fetchClubsList } = useGet(
    API_ENDPOINTS.CLUBS_LIST_WITHOUT_PAGINATE,
  );

  // Load clubs list for club name resolution
  const loadClubsList = async () => {
    try {
      const response = await fetchClubsList();
      if (response?.status === true && response.data) {
        setClubsList(response.data);
      }
    } catch (error) {
      console.error("Error loading clubs:", error);
    }
  };

  // Load item details
  const loadItemDetails = async () => {
    setLoading(true);
    try {
      const endpoint = `${API_ENDPOINTS.CLUB_MIGRATE_DETAIL}/${id}`;
      const response = await getItem(endpoint);

      if (response?.status === true && response.data) {
        setItem(response.data);
      } else {
        Toast.error(t("clubMigrate.failedFetchDetails"));
        navigate("/club-migrate");
      }
    } catch (error) {
      console.error("Error fetching details:", error);
      Toast.error(error.message || t("clubMigrate.failedFetchDetails"));
      navigate("/club-migrate");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      loadItemDetails();
      loadClubsList();
    }
  }, [id]);

  // Helper functions
  const getClubName = (clubId) => {
    if (!clubId) return "N/A";
    const club = clubsList.find((c) => c.id === clubId);
    return club ? club.name : "N/A";
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDateOnly = (dateString) => {
    if (!dateString) return "—";
    const date = new Date(dateString);
    return date.toLocaleDateString(i18n.language === "nl" ? "nl-NL" : "en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  if (loading || itemLoading) return null;

  if (!item) {
    return (
      <Card>
        <div style={{ textAlign: "center", padding: "40px" }}>
          <h3>{t("clubMigrate.noRecordsFound")}</h3>
          <Button type="primary" onClick={() => navigate("/club-migrate")}>
            {t("common.previous")} {t("common.list")}
          </Button>
        </div>
      </Card>
    );
  }

  return (
    <div className="club-migrate-details-page">
      {/* Header with back button and actions */}
      <div
        style={{
          marginBottom: "20px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Space>
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={() => navigate("/club-migrate")}
          >
            {t("common.previous")} {t("common.list")}
          </Button>
          <h2 style={{ margin: 0 }}>{t("clubMigrate.details")}</h2>
        </Space>
        {/* Edit button commented out (can be enabled if needed) */}
        {/* <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`/club-migrate/edit/${item.id}`)}
        >
          {t("common.edit")}
        </Button> */}
      </div>

      {/* Main Content */}
      <Card>
        {/* Title Section with Coupon Code */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            marginBottom: "24px",
          }}
        >
          <div>
            <h1 style={{ margin: 0, marginBottom: "8px" }}>
              {item.coupon_code}
            </h1>
            <Space size="middle">
              <Badge
                status={item.is_active ? "success" : "default"}
                text={
                  item.is_active ? t("common.active") : t("common.inactive")
                }
              />
              {item.is_free && (
                <Tag color="gold" icon={<GiftOutlined />}>
                  {t("clubMigrate.free")}
                </Tag>
              )}
            </Space>
          </div>
        </div>

        {/* Details in Descriptions */}
        <Descriptions bordered column={2}>
          <Descriptions.Item label={t("clubMigrate.couponCode")} span={2}>
            <Space>
              <TagOutlined />
              {item.coupon_code}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("clubMigrate.club")} span={2}>
            {getClubName(item.club)}
          </Descriptions.Item>

          <Descriptions.Item label={t("clubMigrate.assignedSlots")}>
            {item.assigned_slots}
          </Descriptions.Item>

          <Descriptions.Item label={t("clubMigrate.freeUntil")}>
            {item.free_until ? formatDateOnly(item.free_until) : "—"}
          </Descriptions.Item>

          <Descriptions.Item label={t("clubMigrate.isFree")}>
            {item.is_free ? t("common.yes") : t("common.no")}
          </Descriptions.Item>

          <Descriptions.Item label={t("common.status")}>
            <Tag color={item.is_active ? "green" : "default"}>
              {item.is_active ? t("common.active") : t("common.inactive")}
            </Tag>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.createdAt")}>
            <Space>
              <CalendarOutlined />
              {formatDate(item.created_at)}
            </Space>
          </Descriptions.Item>

          <Descriptions.Item label={t("common.lastUpdated")}>
            <Space>
              <ClockCircleOutlined />
              {formatDate(item.updated_at)}
            </Space>
          </Descriptions.Item>
        </Descriptions>
      </Card>
    </div>
  );
};

export default ClubMigrateDetails;
